import {
    CheckOutlined,
    CloseOutlined,
    LocationCityOutlined,
} from "@mui/icons-material";

import Rmodal from "../../../../components/Rmodal";

import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    Paper,
    TextField,
    Typography,
} from "@mui/material";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { selectedCustomerToManagerMapAtom, selectedParametersAtom, userAtom } from "../../../../atoms/atoms";

import {
    CustomerListType,
    DepartmentSimpleType,
    ManagerSimpleType,
    SecretarySimpleType,
    SelectedParametersSimpleType,
    SelectedParametersType,
    UserType,
} from "../../../../types/types";

import {
    customerDefault,
    departmentDefault,
    managerDefault,
    secretaryDefault,
} from "../../../../consts/consts";

import { Toast } from "primereact/toast";
import { useCookies } from "react-cookie";

import dayjs from "dayjs";
import { setToastRefToOpen } from "../../../../functions/functions";

import userFindAllSecretariesListResponse from "../../../../resources/Api/user/userFindAllSecretariesListResponse";
import userFindAllDepartmentsListResponse from "../../../../resources/Api/user/userFindAllDepartmentsListResponse";
import managerFindAllListResponse from "../../../../resources/Api/manager/managerFindAllListResponse";
import { useLocation, useNavigate } from "react-router-dom";
import customerFindAllListResponse from "../../../../resources/Api/customer/customerFindAllListResponse";
import findAllSecretariesByCustomerIdListResponse from "../../../../resources/Api/secretary/findAllSecretariesByCustomerIdListResponse";
import findAllDepartmentsByCustomerIdAndSecretaryIdListResponse from "../../../../resources/Api/department/findAllDepartmentsByCustomerIdAndSecretaryIdListResponse";
import logoutUser from "../../../../resources/Api/authenticator/logoutUser";

const SelectParametersModal = ({
    onSend,
    loading,
    isVisible,
    toast,
}: propTypes) => {
    // atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    const setSelectedParameters = useSetRecoilState<SelectedParametersType>(
        selectedParametersAtom
    );

    const setSelectedCustomerToManagerMap = useSetRecoilState<SelectedParametersSimpleType>(
        selectedCustomerToManagerMapAtom
    );

    // cookies
    const [cookies, setCookies, removeCookies] = useCookies(["selectedParameters", "token", "oldSelectedParameters", "oldToken"]);

    // states
    const [manager, setManager] = useState<ManagerSimpleType>(managerDefault);
    const [customer, setCustomer] = useState<CustomerListType>(customerDefault);
    const [secretary, setSecretary] = useState<SecretarySimpleType>(secretaryDefault);
    const [department, setDepartment] = useState<DepartmentSimpleType>(departmentDefault);

    const [managersList, setManagersList] = useState<Array<ManagerSimpleType>>([]);
    const [secretariesList, setSecretariesList] = useState<Array<SecretarySimpleType>>([]);
    const [departmentsList, setDepartmentsList] = useState<Array<DepartmentSimpleType>>([]);
    const [customersList, setCustomersList] = useState<Array<CustomerListType>>([]);

    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [isToastVisible, setIsToastVisible] = useState(false);

    const [isViewingAll, setIsViewingAll] = useState(false);

    // refs
    const toastRef = useRef<Toast>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const goToRoute = useCallback(
        async (path: string, state?: any) => {
            const hasConection: boolean = window.navigator.onLine;
            if (hasConection) {
                navigate(path, { state: state });
            } else {
                console.log("offline");
            }
        },
        [navigate]
    );

    const onSignOut = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const oldToken: string = cookies.oldToken;
        const oldParameters: SelectedParametersType = cookies.oldSelectedParameters;

        if (token) {
            try {
                if (oldToken) {
                    setCookies("token", oldToken);
                    setCookies("selectedParameters", oldParameters);

                    removeCookies("oldToken");
                    removeCookies("oldSelectedParameters");
                    localStorage.removeItem("impersonating");

                    goToRoute("/");
                } else {
                    try {
                        await logoutUser(getCurrentUser.id, token);
                        removeCookies("token");
                    } catch (error: any) {
                        console.log("Erro ao deslogar:", error.message);
                    }
                }
            } catch (error: any) {
                console.log(error.message);
            } finally {
                setTimeout(() => {
                    window.location.reload();
                }, 2500);
            }
        }
    }, [cookies.token, cookies.oldSelectedParameters, cookies.oldToken, goToRoute, setCookies, removeCookies, getCurrentUser.id]);

    const saveSelectedParametersManager = useCallback(async () => {
        const data: SelectedParametersType = {
            userId: getCurrentUser.id,
            customer: {
                name: manager.name,
                cnpj: manager.cnpj,
                id: manager.id,
                reducedName: manager?.reducedName ?? "-",
                managerId: null,
                useCpf: false,
                cpf: "",
                activated: manager.activated
            },
            secretary: secretaryDefault,
            department: departmentDefault
        };

        if (!data.customer.name) {
            const message = "A seleção de parâmetros é obrigatória, tente novamente.";
            setFieldsIsValid(false);
            setTextValidation(message);
            return;
        }

        setCookies("selectedParameters", data, {
            expires: new Date(dayjs().add(7, "days").toLocaleString()),
        });
        setSelectedParameters(data);
        onSend();
    }, [
        isViewingAll,
        getCurrentUser.id,
        manager,
        onSend,
        setCookies,
        setSelectedParameters,
    ]);

    const saveSelectedParameters = useCallback(async () => {
        const { image, ...rest } = getCurrentUser.customer;

        const data: SelectedParametersType = {
            userId: getCurrentUser.id,
            customer: rest,
            secretary: secretary,
            department: department,
            isViewingAll: isViewingAll
        };

        if ((!data.secretary.name || !data.department.name) && !isViewingAll) {
            const message = "A seleção de parâmetros é obrigatória, tente novamente.";
            setFieldsIsValid(false);
            setTextValidation(message);
            return;
        }
        setCookies("selectedParameters", data, {
            expires: new Date(dayjs().add(7, "days").toLocaleString()),
        });
        setSelectedParameters(data);
        onSend();
    }, [
        getCurrentUser.id,
        getCurrentUser.customer,
        isViewingAll,
        secretary,
        department,
        onSend,
        setCookies,
        setSelectedParameters,
    ]);

    const saveCustomerDataToManagerApp = useCallback(async () => {
        const data: SelectedParametersSimpleType = {
            managerId: -1,
            customerId: customer.id,
            secretaryId: secretary.id,
            departmentId: department.id,
            userId: getCurrentUser.id,
            isViewingAll: isViewingAll,
            name: customer.name,
            reducedName: customer.reducedName,
            image: customer?.image ?? ""
        };
        if ((customer.id > -1 && secretary.id > -1 && department.id > -1) || (isViewingAll && customer.id > -1)) {
            setSelectedCustomerToManagerMap(data);
        } else {
            const message =
                "A seleção de parâmetros é obrigatória, tente novamente.";
            setFieldsIsValid(false);
            setTextValidation(message);
        }
    }, [
        customer?.image,
        customer.name,
        customer.reducedName,
        getCurrentUser?.customer?.id,
        customer.id,
        department.id,
        secretary.id,
        isViewingAll,
        getCurrentUser.id,
        setSelectedCustomerToManagerMap,
    ]);

    const getManagersList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        try {
            const response = await managerFindAllListResponse(token);
            if (response.data.length > 0) {
                setManagersList(response.data);
            }
        } catch (error) {
            console.log(error);
            setManagersList([]);
        }
    }, [cookies.token]);

    useEffect(() => {
        if (getCurrentUser.configs.isManager && location.pathname !== "/tracker") {
            getManagersList();
        }
    }, [getManagersList, getCurrentUser.configs.isManager, location.pathname]);

    const getSecretariesList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        const userId = getCurrentUser.id;
        try {
            const response = await userFindAllSecretariesListResponse(
                token,
                userId
            );
            if (response.data.length > 0) {
                setSecretariesList(response.data);
            }
        } catch (error) {
            console.log(error);
            setSecretariesList([]);
        }
    }, [cookies.token, getCurrentUser.id]);

    useEffect(() => {
        if (!getCurrentUser.configs.isManager) {
            getSecretariesList();
        }
    }, [getSecretariesList, getCurrentUser.configs.isManager]);

    const getDepartmentsList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        const userId = getCurrentUser.id;
        try {
            const response =
                await userFindAllDepartmentsListResponse(
                    token,
                    userId,
                    secretary?.id
                );
            setDepartmentsList(response.data);
        } catch (error) {
            console.log(error);
            setDepartmentsList([]);
        }
    }, [cookies.token, getCurrentUser.id, secretary.id]);

    useEffect(() => {
        if (!getCurrentUser.configs.isManager && secretary?.name) {
            getDepartmentsList();
        }
    }, [getDepartmentsList, secretary?.name, getCurrentUser.configs.isManager]);

    useEffect(() => {
        const ref = toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                "warn",
                undefined,
                3000,
                false
            );
        }
    }, [validationText, isFieldsValid, isToastVisible]);

    const getCustomersList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        try {
            const response = await customerFindAllListResponse(token, cookies.selectedParameters.customer.id);
            setCustomersList(response.data);
        } catch (error) {
            console.log(error);
            setCustomersList([]);
        }
    }, [cookies.token, cookies.selectedParameters?.customer?.id]);

    useEffect(() => {
        if (getCurrentUser.configs.isManager && location.pathname === "/tracker") {
            getCustomersList();
        }
    }, [getCustomersList, getCurrentUser.configs.isManager, location.pathname]);

    const getSecretariesManagerList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        const customerId = customer.id;

        try {
            const response = await findAllSecretariesByCustomerIdListResponse(
                token,
                customerId
            );
            setSecretariesList(response.data);
        } catch (error) {
            console.log(error);
            setSecretariesList([]);
        }
    }, [cookies.token, customer?.id, location.pathname]);

    useEffect(() => {
        if (getCurrentUser.configs.isManager && customer?.name) {
            getSecretariesManagerList();
        }
    }, [getSecretariesManagerList, customer?.name, getCurrentUser.configs.isManager]);

    const getDepartmentsManagerList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        const customerId = customer.id;
        const secretaryId = secretary.id;

        try {
            const response =
                await findAllDepartmentsByCustomerIdAndSecretaryIdListResponse(
                    token,
                    customerId,
                    secretaryId
                );
            setDepartmentsList(response.data);
        } catch (error) {
            console.log(error);
            setDepartmentsList([]);
        }
    }, [cookies.token, customer.id, secretary.id, getCurrentUser.configs.isManager]);

    useEffect(() => {
        if (getCurrentUser.configs.isManager && customer?.name && secretary?.name) {
            getDepartmentsManagerList();
        }
    }, [getDepartmentsManagerList, secretary?.name, customer?.name, getCurrentUser.configs.isManager]);

    const fieldSx = {
        width: "100%",
        "& .MuiOutlinedInput-root": {
            minHeight: 56,
            borderRadius: 2,
            backgroundColor: "#ffff",
            transition: "box-shadow 200ms ease, border-color 200ms ease",
            "& fieldset": {
                borderColor: "rgba(0, 63, 74, 0.24)",
            },
            "&:hover fieldset": {
                borderColor: "#006C77",
            },
            "&.Mui-focused": {
                boxShadow: "0 0 0 3px rgba(0, 183, 0, 0.12)",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#00B700",
            },
            "&.Mui-disabled": {
                backgroundColor: "rgba(0, 63, 74, 0.04)",
            },
        },
        "& .MuiInputLabel-root": {
            fontFamily: "Prometo",
        },
    };

    return (
        <>
            <Toast
                ref={toastRef}
                style={{ fontFamily: "Prometo", color: "black" }}
                position="top-right"
                onShow={() => {
                    setFieldsIsValid(true);
                    setIsToastVisible(true);

                    if (validationText) {
                        setTimeout(() => {
                            setTextValidation("");
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            <Rmodal
                open={isVisible && !loading}
                hiddenBackdrop={getCurrentUser.configs.isManager}
                toast={toast}
                style={{ display: !isVisible ? "none" : "" }}
            >
                <Paper
                    className="parameters-container tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth dark:tw-bg-slate-900"
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        display: "flex",
                        flexDirection: "column",
                        width: { xs: "calc(100vw - 24px)", sm: "min(86vw, 680px)" },
                        maxWidth: 680,
                        maxHeight: "calc(100dvh - 24px)",
                        overflow: "hidden",
                        border: "1px solid rgba(0, 63, 74, 0.10)",
                        borderRadius: 4,
                        backgroundColor: "#ffff",
                        boxShadow: "0 24px 70px rgba(0, 0, 0, 0.28)",
                        color: "black",
                        fontFamily: "Prometo",
                    }}
                >
                    <Box
                        className="tw-flex tw-items-center tw-gap-4 tw-bg-gtr-sidebar tw-px-5 tw-py-4 tw-text-white"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            paddingX: { xs: 2, sm: 3 },
                            paddingY: 2,
                            color: "#ffff",
                            backgroundImage: "linear-gradient(to right, #006C77, #09414A)",
                        }}
                    >
                        <Box
                            className="tw-flex tw-h-14 tw-w-14 tw-flex-none tw-items-center tw-justify-center tw-rounded-xl tw-bg-white/10"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 56,
                                height: 56,
                                flexShrink: 0,
                                borderRadius: 3,
                                backgroundColor: "rgba(255, 255, 255, 0.10)",
                            }}
                        >
                            <Box
                                component="img"
                                src={require("../../../../assets/files/tracker.png")}
                                alt="GTR"
                                sx={{ width: 46, height: 46, objectFit: "contain" }}
                            />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: "#ffff", fontFamily: "Prometo", fontSize: { xs: 18, sm: 21 }, fontWeight: 700 }}>
                                Selecionar parâmetros
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        className="parameters-content tw-flex-1 tw-overflow-y-auto tw-bg-white tw-p-4 dark:tw-bg-slate-900 sm:tw-p-6"
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            padding: { xs: 2, sm: 3 },
                            backgroundColor: "#ffff",
                            scrollbarWidth: "thin",
                            scrollbarColor: "#006C77 transparent",
                        }}
                    >
                        {!getCurrentUser.configs.isManager && (
                            <Chip
                                icon={<LocationCityOutlined sx={{ color: "#003F4A !important" }} />}
                                label={getCurrentUser.customer?.name || getCurrentUser.customer?.reducedName}
                                variant="outlined"
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    width: "100%",
                                    minHeight: 44,
                                    height: "auto",
                                    marginBottom: 3,
                                    paddingY: 0.5,
                                    borderColor: "rgba(0, 63, 74, 0.18)",
                                    borderRadius: 2,
                                    color: "#003F4A",
                                    backgroundColor: "rgba(0, 63, 74, 0.04)",
                                    fontFamily: "Prometo",
                                    fontSize: { xs: 14, sm: 15 },
                                    "& .MuiChip-label": {
                                        whiteSpace: "normal",
                                        textAlign: "center",
                                    },
                                }}
                            />
                        )}

                        <Box className="tw-grid tw-grid-cols-1 tw-gap-4" sx={{ display: "grid", gap: 2 }}>
                            {getCurrentUser.configs.isManager && location.pathname === "/tracker" && (
                                <Autocomplete
                                    id="parameters-customer"
                                    options={customersList}
                                    value={customer}
                                    onChange={(_, option) => {
                                        setCustomer(option ?? customerDefault);
                                        setSecretary(secretaryDefault);
                                        setDepartment(departmentDefault);
                                    }}
                                    getOptionLabel={(option) => `${option.reducedName}`}
                                    noOptionsText="Nada encontrado"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Cliente"
                                            placeholder="Selecione um cliente"
                                            required
                                            autoComplete="off"
                                            InputLabelProps={{ shrink: true }}
                                            sx={fieldSx}
                                        />
                                    )}
                                />
                            )}

                            {(!getCurrentUser.configs.isManager || location.pathname === "/tracker") && (
                                <>
                                    <Autocomplete
                                        id="parameters-secretary"
                                        disabled={isViewingAll}
                                        options={secretariesList}
                                        value={secretary}
                                        onChange={(_, option) => {
                                            setSecretary(option ?? secretaryDefault);
                                            setDepartment(departmentDefault);
                                        }}
                                        getOptionLabel={(option) => `${option.reducedName}`}
                                        noOptionsText="Nada encontrado"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Secretaria"
                                                placeholder="Selecione uma secretaria"
                                                disabled={isViewingAll}
                                                required={!isViewingAll}
                                                autoComplete="off"
                                                InputLabelProps={{ shrink: true }}
                                                sx={fieldSx}
                                            />
                                        )}
                                    />
                                    <Autocomplete
                                        id="parameters-department"
                                        disabled={isViewingAll}
                                        options={departmentsList}
                                        value={department}
                                        onChange={(_, option) => {
                                            setDepartment(option ?? departmentDefault);
                                        }}
                                        getOptionLabel={(option) => `${option.reducedName}`}
                                        noOptionsText="Nada encontrado"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Departamento"
                                                placeholder="Selecione um departamento"
                                                disabled={isViewingAll}
                                                required={!isViewingAll}
                                                autoComplete="off"
                                                InputLabelProps={{ shrink: true }}
                                                sx={fieldSx}
                                            />
                                        )}
                                    />
                                    <FormControlLabel
                                        sx={{
                                            marginX: 0,
                                            paddingX: 1,
                                            paddingY: 0.5,
                                            borderRadius: 2,
                                            transition: "background-color 200ms ease",
                                            "&:hover": { backgroundColor: "rgba(0, 63, 74, 0.05)" },
                                            "& .MuiFormControlLabel-label": { fontFamily: "Prometo" },
                                        }}
                                        control={
                                            <Checkbox
                                                checked={isViewingAll}
                                                onChange={(event) => {
                                                    setIsViewingAll(event.target.checked);
                                                    setSecretary(secretaryDefault);
                                                    setDepartment(departmentDefault);
                                                }}
                                                sx={{
                                                    color: "#006C77",
                                                    "&.Mui-checked": { color: "#00B700" },
                                                }}
                                            />
                                        }
                                        label="Acessar todos"
                                    />
                                </>
                            )}

                            {getCurrentUser.configs.isManager && location.pathname !== "/tracker" && (
                                <Autocomplete
                                    id="parameters-manager"
                                    options={managersList}
                                    value={manager}
                                    onChange={(_, option) => {
                                        setManager(option ?? managerDefault);
                                    }}
                                    getOptionLabel={(option) => `${option.reducedName}`}
                                    noOptionsText="Nada encontrado"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Administradora"
                                            placeholder="Selecione uma administradora"
                                            required
                                            autoComplete="off"
                                            InputLabelProps={{ shrink: true }}
                                            sx={fieldSx}
                                        />
                                    )}
                                />
                            )}
                        </Box>
                    </Box>

                    <Box
                        className="parameters-footer tw-flex tw-flex-col-reverse tw-gap-2 tw-border-t tw-border-black/10 tw-bg-white/70 tw-p-4 dark:tw-border-slate-700 dark:tw-bg-slate-900 sm:tw-flex-row sm:tw-justify-end"
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column-reverse", sm: "row" },
                            justifyContent: { sm: "flex-end" },
                            gap: 1,
                            padding: 2,
                            borderTop: "1px solid rgba(0, 0, 0, 0.10)",
                            backgroundColor: "#ffff",
                        }}
                    >
                        {location.pathname !== "/tracker" && (
                            <Button
                                type="button"
                                variant="contained"
                                startIcon={<CloseOutlined />}
                                onClick={() => {
                                    if (!isToastVisible) {
                                        onSignOut();
                                    }
                                }}
                                sx={{
                                    minWidth: 132,
                                    minHeight: 44,
                                    borderRadius: 2,
                                    color: "#ffff",
                                    backgroundColor: "#dc3545",
                                    fontFamily: "Prometo",
                                    textTransform: "none",
                                    boxShadow: "none",
                                    transition: "transform 200ms ease, box-shadow 200ms ease",
                                    "&:hover": {
                                        backgroundColor: "#dc3545",
                                        boxShadow: "0 8px 18px rgba(220, 53, 69, 0.22)",
                                        transform: "translateY(-1px)",
                                    },
                                }}
                            >
                                Sair
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="contained"
                            startIcon={<CheckOutlined />}
                            onClick={() => {
                                if (getCurrentUser.configs.isManager && location.pathname === "/tracker") {
                                    saveCustomerDataToManagerApp();
                                } else if (getCurrentUser.configs.isManager) {
                                    saveSelectedParametersManager();
                                } else {
                                    saveSelectedParameters();
                                }
                            }}
                            sx={{
                                minWidth: 132,
                                minHeight: 44,
                                borderRadius: 2,
                                color: "#ffff",
                                backgroundColor: "#00B700",
                                fontFamily: "Prometo",
                                textTransform: "none",
                                boxShadow: "none",
                                transition: "transform 200ms ease, box-shadow 200ms ease",
                                "&:hover": {
                                    backgroundColor: "#00B700",
                                    boxShadow: "0 8px 18px rgba(0, 183, 0, 0.22)",
                                    transform: "translateY(-1px)",
                                },
                            }}
                        >
                            Confirmar
                        </Button>
                    </Box>
                </Paper>
            </Rmodal>
        </>
    );
};

type propTypes = {
    isVisible: boolean;
    onClose: () => void;
    children?: JSX.Element;
    toast?: JSX.Element;
    onSend: () => void;
    loading: boolean;
    handleChange: (...args: any) => void;
    fieldValue: string;
};

export default SelectParametersModal;
